<template>
  <div class="wifi-admin-page">
    <section class="hero-panel">
      <div>
        <p class="eyebrow">QRWiFi</p>
        <h1>店铺管理</h1>
        <p class="description">复刻小程序“碰联生活”设计稿的后台管理端，统一管理店铺、WiFi密码与连接数据。</p>
      </div>
    </section>

    <section class="stats-grid">
      <div class="stat-card stat-card--blue">
        <div class="stat-card__label">店铺数量</div>
        <div class="stat-card__value">{{ summary.codeCount }}</div>
      </div>
      <div class="stat-card stat-card--orange">
        <div class="stat-card__label">昨日连接</div>
        <div class="stat-card__value">{{ summary.yesterdayConnectCount }}</div>
      </div>
      <div class="stat-card stat-card--purple">
        <div class="stat-card__label">累计连接</div>
        <div class="stat-card__value">{{ summary.totalConnectCount }}</div>
      </div>
    </section>

    <section class="table-card">
      <div class="toolbar">
        <div>
          <h2>店铺列表</h2>
          <p>对齐小程序“我的店铺”列表，支持搜索、修改、停用和删除。</p>
        </div>
        <div class="toolbar__actions">
          <el-input
            v-model="query.keyword"
            clearable
            placeholder="搜索店铺名称或WiFi名"
            class="search-input"
            @keyup.enter="loadData"
            @clear="loadData"
          />
          <el-button @click="loadData">搜索</el-button>
        </div>
      </div>

      <el-table v-loading="loading" :data="tableData" border stripe>
        <el-table-column prop="storeName" label="店铺名称" min-width="180" />
        <el-table-column prop="ssid" label="WiFi名称 / SSID" min-width="180" />
        <el-table-column prop="password" label="WiFi密码" min-width="150" />
        <el-table-column prop="yesterdayConnectCount" label="昨日连接" width="110" align="right" />
        <el-table-column prop="totalConnectCount" label="累计连接" width="110" align="right" />
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.enabled === 1 ? 'success' : 'info'">{{ row.enabled === 1 ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openEdit(row)">修改</el-button>
            <el-button link type="warning" @click="toggleEnabled(row)">{{ row.enabled === 1 ? '停用' : '启用' }}</el-button>
            <el-button link type="danger" @click="remove(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <el-pagination
          v-model:current-page="query.page"
          v-model:page-size="query.pageSize"
          background
          layout="prev, pager, next, sizes, total"
          :total="total"
          @current-change="loadData"
          @size-change="loadData"
        />
      </div>
    </section>

    <el-dialog v-model="dialogVisible" title="修改店铺" width="520px">
      <el-form :model="form" label-width="110px">
        <el-form-item label="店铺名称" required>
          <el-input v-model="form.storeName" placeholder="请输入店铺名称" />
        </el-form-item>
        <el-form-item label="WiFi名称" required>
          <el-input v-model="form.ssid" placeholder="请输入SSID" />
        </el-form-item>
        <el-form-item label="WiFi密码" required>
          <el-input v-model="form.password" placeholder="请输入WiFi密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useApi } from '@/hooks/useApi';
import type { QrWifiCode, QrWifiSummary } from '@/api';

const loading = ref(false);
const dialogVisible = ref(false);
const tableData = ref<QrWifiCode[]>([]);
const total = ref(0);
const summary = reactive<QrWifiSummary>({
  codeCount: 0,
  yesterdayConnectCount: 0,
  totalConnectCount: 0,
});
const query = reactive({
  keyword: '',
  page: 1,
  pageSize: 10,
});
const form = reactive<QrWifiCode>({
  storeName: '',
  ssid: '',
  password: '',
  enabled: 1,
});

const resetForm = () => {
  form.id = undefined;
  form.storeName = '';
  form.ssid = '';
  form.password = '';
  form.enabled = 1;
};

const loadSummary = async() => {
  const res = await useApi().qrWifiApi.getSummary();
  if (res.success && res.data) {
    Object.assign(summary, res.data);
  }
};

const loadData = async() => {
  loading.value = true;
  try {
    const res = await useApi().qrWifiApi.pageWifiCodes(query.keyword, query.page, query.pageSize);
    if (res.success && res.data) {
      tableData.value = res.data.data;
      total.value = res.data.total;
    }
    await loadSummary();
  } finally {
    loading.value = false;
  }
};


const openEdit = (row: QrWifiCode) => {
  Object.assign(form, row);
  dialogVisible.value = true;
};

const assertForm = () => {
  if (!form.storeName || !form.ssid || !form.password) {
    ElMessage.warning('请填写店铺名称、WiFi名称和WiFi密码');
    return false;
  }
  return true;
};

const save = async() => {
  if (!assertForm()) {
    return;
  }
  if (!form.id) {
    ElMessage.warning('后台只允许修改已有店铺');
    return;
  }
  const res = await useApi().qrWifiApi.updateWifiCode(form);
  if (res.success) {
    ElMessage.success('保存成功');
    dialogVisible.value = false;
    await loadData();
  }
};

const toggleEnabled = async(row: QrWifiCode) => {
  await useApi().qrWifiApi.updateWifiCode({
    id: row.id,
    enabled: row.enabled === 1 ? 0 : 1,
  });
  ElMessage.success('状态已更新');
  await loadData();
};

const remove = async(row: QrWifiCode) => {
  await ElMessageBox.confirm(`确定删除「${row.storeName}」的店铺吗？`, '删除确认', {
    type: 'warning',
  });
  if (!row.id) {
    return;
  }
  await useApi().qrWifiApi.deleteWifiCode(row.id);
  ElMessage.success('删除成功');
  await loadData();
};

onMounted(loadData);
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
